import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { BellRinging, X } from '@phosphor-icons/react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { useAuthOptional } from '../../contexts/AuthContext';
import { addSearch } from '../../redux/features/savedSearchesSlice';
import { openSignInPrompt } from '../../redux/features/authPrompt/authPromptSlice';
import { useSavedSearchAccountSync } from '../../hooks/useSavedSearchAccountSync';
import { useEmailAlertsStatus } from '../../hooks/useEmailAlertsStatus';
import { subscribeToMasterAlerts } from '../../utils/email-alerts/subscribe-to-master-alerts';
import { writeMarketingConsent } from '../../utils/marketing-consent/write-marketing-consent';
import {
  consumePendingAlertIntent,
  setPendingAlertIntent,
} from '../../utils/engagement/pending-alert-intent';
import {
  markSrpAlertCtaDismissedThisSession,
  shouldShowSrpAlertCta,
} from '../../utils/engagement/srp-alert-cta-session';
import { MarketingConsentCheckbox } from './MarketingConsentCheckbox';
import { Link } from '../Link';
import { trackEvent } from '../../utils/analytics/analytics';
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';

const CONSENT_SOURCE = 'srp-alert-cta';

const messages = defineMessages({
  bannerText: {
    id: 'srpAlertCta.bannerText',
    defaultMessage: 'Want to hear about new jobs like this?',
    description: 'Short banner text inviting the user to set up a job alert for the current search',
  },
  ctaButton: {
    id: 'srpAlertCta.ctaButton',
    defaultMessage: 'Get email alerts',
    description: 'Button that opens (or triggers) job-alert setup for the current search',
  },
  dismissLabel: {
    id: 'srpAlertCta.dismissLabel',
    defaultMessage: 'Dismiss',
    description: 'Accessibility label for the close button on the SRP alert CTA',
  },
  unauthConsentCopy: {
    id: 'srpAlertCta.unauthConsentCopy',
    defaultMessage: "We'll email you when new jobs matching this search appear. Create a free account to turn it on — no other emails unless you say so.",
    description: 'Consent copy shown in the expanded SRP alert CTA before an unauthenticated user signs in',
  },
  continueButton: {
    id: 'srpAlertCta.continueButton',
    defaultMessage: 'Continue with email',
    description: 'Button that stores the alert intent and opens the sign-in prompt',
  },
  successText: {
    id: 'srpAlertCta.successText',
    defaultMessage: "You're set — we'll email you about jobs like this.",
    description: 'Confirmation shown once alerts are enabled for the current search',
  },
  manageLink: {
    id: 'srpAlertCta.manageLink',
    defaultMessage: 'Manage alerts',
    description: 'Link from the SRP alert success state to the saved-searches/alerts management page',
  },
  pendingText: {
    id: 'srpAlertCta.pendingText',
    defaultMessage: 'Almost there — check your inbox to confirm your email and activate alerts.',
    description: 'Shown after enabling alerts when the account email still needs confirmation',
  },
  errorText: {
    id: 'srpAlertCta.errorText',
    defaultMessage: "Something went wrong — we couldn't turn on alerts. Please try again.",
    description: 'Shown when enabling alerts for the current search fails',
  },
});

export interface SrpAlertCtaProps {
  /** Only ever shown once a real search (query or filter) is active — never on the bare landing state. */
  hasActiveSearch: boolean;
}

/**
 * Contextual "get alerted" CTA rendered on the SRP (never on the bare landing state — see
 * hasActiveSearch). Doubles as the account-creation wedge: unauthenticated users are asked for
 * an account only at the moment they've expressed intent to be alerted, and a separate, unticked
 * marketing-consent checkbox is offered at that same moment (never bundled into alert consent).
 */
export function SrpAlertCta({ hasActiveSearch }: SrpAlertCtaProps) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const dispatch = useAppDispatch();
  const auth = useAuthOptional();
  const isAuthenticated = Boolean(auth?.isAuthenticated);
  const uiLocale = (['en', 'fi', 'sv'].includes(intl.locale) ? intl.locale : 'en') as 'en' | 'fi' | 'sv';

  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setVisible(shouldShowSrpAlertCta());
  }, []);

  const [expanded, setExpanded] = useState(false);
  const [wantsMarketing, setWantsMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const currentUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.pathname}${window.location.search}`;
  }, []);

  const savedSearches = useAppSelector((state) => state.recentSearches?.savedSearches ?? []);
  const accountSync = useSavedSearchAccountSync(isAuthenticated, savedSearches);
  const emailAlertsStatus = useEmailAlertsStatus(isAuthenticated);

  const enableAlertForCurrentSearch = useCallback(
    async (url: string, shouldOptIntoMarketing: boolean) => {
      dispatch(addSearch(url));
      await accountSync.mutateItem(url, { alertEnabled: true });
      if (emailAlertsStatus.subscription?.status !== 'active') {
        await subscribeToMasterAlerts(uiLocale);
        await emailAlertsStatus.refresh();
      }
      if (shouldOptIntoMarketing) {
        await writeMarketingConsent(true, CONSENT_SOURCE);
      }
    },
    [dispatch, accountSync, emailAlertsStatus, uiLocale]
  );

  // Resolve an alert request made as a guest once the Auth0 redirect brings the user back.
  useEffect(() => {
    if (!isAuthenticated) return;
    const intent = consumePendingAlertIntent();
    if (!intent) return;
    setBusy(true);
    setFailed(false);
    void enableAlertForCurrentSearch(intent.url, intent.wantsMarketing)
      .catch((error) => {
        setFailed(true);
        if (import.meta.env.DEV) {
          console.error('[srp-alert-cta] Failed to resolve pending alert intent', error);
        }
      })
      .finally(() => setBusy(false));
    // Intentionally narrow: this is a one-shot resolution keyed off the auth transition, not the
    // (identity-unstable) hook results referenced inside enableAlertForCurrentSearch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    trackEvent({ name: 'job_alert_cta_interaction', params: { action: 'dismiss', locale: uiLocale } });
    markSrpAlertCtaDismissedThisSession();
  }, [uiLocale]);

  const handleAuthenticatedClick = useCallback(async () => {
    setBusy(true);
    setFailed(false);
    try {
      await enableAlertForCurrentSearch(currentUrl, false);
    } catch (error) {
      setFailed(true);
      if (import.meta.env.DEV) {
        console.error('[srp-alert-cta] Failed to enable alerts', error);
      }
    } finally {
      setBusy(false);
    }
  }, [enableAlertForCurrentSearch, currentUrl]);

  const handleExpand = useCallback(() => {
    trackEvent({ name: 'job_alert_cta_interaction', params: { action: 'expand', locale: uiLocale } });
    setExpanded(true);
  }, [uiLocale]);

  const handleContinueAsGuest = useCallback(() => {
    trackEvent({ name: 'job_alert_cta_interaction', params: { action: 'continue_with_email', locale: uiLocale } });
    setPendingAlertIntent({ url: currentUrl, wantsMarketing });
    dispatch(openSignInPrompt({ reason: 'create-alert' }));
  }, [dispatch, currentUrl, wantsMarketing, uiLocale]);

  if (!visible || !hasActiveSearch || !auth || auth.isLoading) {
    return null;
  }

  const serverItem = accountSync.getServerItem(currentUrl);
  const alreadyActive =
    isAuthenticated &&
    accountSync.synced &&
    !emailAlertsStatus.loading &&
    serverItem?.alertEnabled === true &&
    emailAlertsStatus.subscription?.status === 'active';
  const pendingConfirmation =
    isAuthenticated &&
    accountSync.synced &&
    !emailAlertsStatus.loading &&
    serverItem?.alertEnabled === true &&
    emailAlertsStatus.subscription?.status === 'pendingConfirmation';

  if (isAuthenticated && (!accountSync.synced || emailAlertsStatus.loading)) {
    return null;
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-xl border border-jc-border/80 bg-jc-surface/95 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      role="region"
      aria-label={formatMessage(messages.bannerText)}
    >
      <div className="flex items-start gap-2">
        <BellRinging className="mt-0.5 h-5 w-5 flex-shrink-0 text-sky-600 dark:text-sky-400" weight="fill" aria-hidden />
        <div className="text-sm text-jc-ink">
          {alreadyActive ? (
            <p>
              <Msg {...messages.successText} />{' '}
              <Link href="/tools/searches" locale={uiLocale} className="font-semibold text-[#3B5E45] underline">
                <Msg {...messages.manageLink} />
              </Link>
            </p>
          ) : pendingConfirmation ? (
            <p className="text-amber-700 dark:text-amber-400">
              <Msg {...messages.pendingText} />
            </p>
          ) : expanded && !isAuthenticated ? (
            <div className="max-w-md space-y-2">
              <p>
                <Msg {...messages.unauthConsentCopy} />
              </p>
              <MarketingConsentCheckbox checked={wantsMarketing} onChange={setWantsMarketing} disabled={busy} />
              <button
                type="button"
                onClick={handleContinueAsGuest}
                disabled={busy}
                className="rounded-md bg-[#1a5632] px-4 py-2 text-sm font-semibold text-white hover:bg-[#14432a] disabled:opacity-50"
              >
                <Msg {...messages.continueButton} />
              </button>
            </div>
          ) : (
            <p>
              <Msg {...messages.bannerText} />
            </p>
          )}
          {failed && (
            <p className="mt-1 text-red-600">
              <Msg {...messages.errorText} />
            </p>
          )}
        </div>
      </div>

      {!alreadyActive && !pendingConfirmation && !(expanded && !isAuthenticated) && (
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <button
            type="button"
            onClick={isAuthenticated ? handleAuthenticatedClick : handleExpand}
            disabled={busy}
            className="rounded-md bg-[#1a5632] px-4 py-2 text-sm font-semibold text-white hover:bg-[#14432a] disabled:opacity-50"
          >
            <Msg {...messages.ctaButton} />
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={formatMessage(messages.dismissLabel)}
            className="rounded-full p-1.5 text-jc-muted transition hover:bg-jc-page focus:outline-none focus-visible:ring-2 focus-visible:ring-jc-cta/50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
